import React, { useEffect, useState, useMemo } from "react";
import Select from "react-select";
import { Global } from "../helpers/Global";

export const GestionUsuarios2 = () => {
  // "dni" | "mail"
  const PRIMARY_FIELD = (Global.loginPrimary || "dni").toLowerCase();
  const REQUIRE_MAIL = PRIMARY_FIELD === "mail";
  const REQUIRE_DNI  = PRIMARY_FIELD === "dni";

  const [usuarios, setUsuarios] = useState([]);
  const [usuariosLocal, setUsuariosLocal] = useState([]);

  // Filtros
  const [filtroRol, setFiltroRol] = useState("");
  const [filtroManager, setFiltroManager] = useState("");
  const [mostrarDesactivados, setMostrarDesactivados] = useState(false);
  const [filtroUsuario, setFiltroUsuario] = useState("");

  // Orden
  const [orden, setOrden] = useState({ campo: "id", ascendente: true });

  // Modales
  const [modalRol, setModalRol] = useState(false);
  const [modalManager, setModalManager] = useState(false);
  const [modalEditar, setModalEditar] = useState(false);

  // Edición
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
  const [nuevoRol, setNuevoRol] = useState("");
  const [nuevoManager, setNuevoManager] = useState("");
  const [nuevoApellidos, setNuevoApellidos] = useState("");
  const [nuevoDni, setNuevoDni] = useState("");
  const [nuevoTelefono, setNuevoTelefono] = useState("");
  const [nuevoNombre, setNuevoNombre] = useState("");
  const [nuevoEmail, setNuevoEmail] = useState("");
  const [errorEmail, setErrorEmail] = useState("");
  const [errorMensaje, setErrorMensaje] = useState(""); // errores generales (DNI/Email obligatorio)
  const [avatarFileEdit, setAvatarFileEdit] = useState(null); // 🆕 archivo de avatar en edición

  useEffect(() => {
    fetchUsuarios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsuarios = async () => {
    try {
      const res = await fetch(`${Global.url}usuarios/todos`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!res.ok) {
        console.error("Error al obtener usuarios:", res.status);
        return;
      }
      const data = await res.json();
      const procesados = resolverManagers(data);
      setUsuarios(procesados);
      setUsuariosLocal(procesados);
    } catch (error) {
      console.error("Error al obtener usuarios:", error);
    }
  };

  const resolverManagers = (lista) => {
    const mapNombre = Object.fromEntries(
      (lista || []).map((u) => [u.id, `${u.nombre} ${u.apellidos || ""}`.trim()])
    );
    return (lista || []).map((u) => ({
      ...u,
      manager: u.gerente_id ? mapNombre[u.gerente_id] || "" : "",
    }));
  };

  const valueFrom = (opts, v) =>
    v || v === 0 ? opts.find((o) => String(o.value) === String(v)) ?? null : null;

  const rolOptions = [
    { value: "", label: "Todos" },
    { value: "usuario", label: "Usuario" },
    { value: "gerente", label: "Gerente" },
    { value: "admin", label: "Administrador" },
  ];

  const managerOptions = useMemo(() => {
    const gerentes = usuariosLocal.filter((u) => u.rol === "gerente");
    const uniq = new Map();
    gerentes.forEach((g) => uniq.set(g.id, `${g.nombre} ${g.apellidos || ""}`.trim()));
    return [
      { value: "", label: "Todos" },
      { value: "none", label: "Sin manager" },
      ...Array.from(uniq.entries())
        .map(([id, label]) => ({ value: String(id), label }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [usuariosLocal]);

  // Opciones usuario (select filtro)
  const usuarioOptions = useMemo(() => {
    return [
      { value: "", label: "Todos" },
      ...usuariosLocal.map((u) => ({
        value: String(u.id),
        label: `${u.nombre} ${u.apellidos || ""} (${u.email || u.dni || ""})`.trim(),
      })),
    ].sort((a, b) => a.label.localeCompare(b.label));
  }, [usuariosLocal]);

  // ---------- helpers avatar ----------
  const getAvatarUrl = (usuario) => {
    if (!usuario?.avatar_url) return null;
    // avatar_url viene como "uploads/avatars/xxx.jpg"
    return `${Global.url}${usuario.avatar_url}`;
  };

  const getIniciales = (usuario) => {
    const nombre = (usuario?.nombre || "").trim();
    const apellidos = (usuario?.apellidos || "").trim();
    const n1 = nombre ? nombre.split(" ")[0][0] : "";
    const a1 = apellidos ? apellidos.split(" ")[0][0] : "";
    const combo = (n1 + a1).toUpperCase();
    if (combo) return combo;
    if (nombre) return nombre[0].toUpperCase();
    return "?";
  };

  // ---------- Filtros / Orden ----------
  const usuariosFiltrados = useMemo(() => {
    return usuariosLocal.filter((u) => {
      const byRol = !filtroRol || u.rol === filtroRol;

      let byManager = true;
      if (filtroManager === "none") {
        byManager = !u.gerente_id;
      } else if (filtroManager) {
        byManager = Number(u.gerente_id) === Number(filtroManager);
      }

      const byActivo = mostrarDesactivados ? true : u.activo !== false;

      let byUsuario = true;
      if (filtroUsuario) {
        byUsuario = String(u.id) === String(filtroUsuario);
      }

      return byRol && byManager && byActivo && byUsuario;
    });
  }, [usuariosLocal, filtroRol, filtroManager, mostrarDesactivados, filtroUsuario]);

  const usuariosOrdenados = useMemo(() => {
    return [...usuariosFiltrados].sort((a, b) => {
      let valA = a[orden.campo] ?? "";
      let valB = b[orden.campo] ?? "";

      if (orden.campo === "manager") {
        valA = a.manager || "";
        valB = b.manager || "";
      }

      if (typeof valA === "string" && typeof valB === "string") {
        return orden.ascendente
          ? valA.localeCompare(valB, undefined, { sensitivity: "base" })
          : valB.localeCompare(valA, undefined, { sensitivity: "base" });
      }
      return orden.ascendente ? Number(valA) - Number(valB) : Number(valB) - Number(valA);
    });
  }, [usuariosFiltrados, orden]);

  const manejarOrden = (campo) => {
    setOrden((prev) => ({
      campo,
      ascendente: prev.campo === campo ? !prev.ascendente : true,
    }));
  };

  // ---------- Modales (abrir) ----------
  const abrirModalRol = (usuario) => {
    setUsuarioSeleccionado(usuario);
    setNuevoRol(usuario.rol || "");
    setModalRol(true);
  };

  const abrirModalManager = (usuario) => {
    setUsuarioSeleccionado(usuario);
    setNuevoManager(usuario.gerente_id || "");
    setModalManager(true);
  };

  const abrirModalEditar = (usuario) => {
    setUsuarioSeleccionado(usuario);
    setNuevoNombre(usuario.nombre || "");
    setNuevoEmail(usuario.email || "");
    setNuevoDni(usuario.dni || "");
    setNuevoApellidos(usuario.apellidos || "");
    setNuevoTelefono(usuario.telefono || "");
    setErrorEmail("");
    setErrorMensaje("");
    setAvatarFileEdit(null); // 🆕 limpiamos selección previa de avatar
    setModalEditar(true);
  };

  // ---------- Acciones ----------
  const actualizarRol = async () => {
    if (!nuevoRol) return;
    try {
      const res = await fetch(`${Global.url}usuarios/rol/${usuarioSeleccionado.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ rol: nuevoRol }),
      });
      if (res.ok) await fetchUsuarios();
    } catch (e) {
      console.error(e);
    } finally {
      setModalRol(false);
    }
  };

  const asignarManager = async () => {
    try {
      const res = await fetch(`${Global.url}usuarios/manager/${usuarioSeleccionado.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ gerente_id: nuevoManager || null }),
      });
      if (res.ok) await fetchUsuarios();
    } catch (e) {
      console.error(e);
    } finally {
      setModalManager(false);
    }
  };

  const validarEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // 🆕 cambio de avatar en el modal de edición
  const onAvatarEditChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setAvatarFileEdit(null);
      return;
    }
    if (!file.type.startsWith("image/")) {
      setErrorMensaje("El archivo seleccionado no es una imagen.");
      setAvatarFileEdit(null);
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      setErrorMensaje("La imagen no puede superar los 3MB.");
      setAvatarFileEdit(null);
      return;
    }
    setErrorMensaje("");
    setAvatarFileEdit(file);
  };

  const editarUsuario = async () => {
    setErrorEmail("");
    setErrorMensaje("");

    const dniLimpio = (nuevoDni || "").toUpperCase().replace(/\s|-/g, "");
    const emailLimpio = (nuevoEmail || "").trim();
    const emailFinal = emailLimpio === "" ? null : emailLimpio;

    // Validación dinámica según versión
    if (REQUIRE_MAIL) {
      if (!emailFinal) {
        setErrorMensaje("El email es obligatorio en esta versión.");
        return;
      }
      if (!validarEmail(emailFinal)) {
        setErrorEmail("Correo electrónico no válido");
        return;
      }
    } else { // REQUIRE_DNI
      if (!dniLimpio) {
        setErrorMensaje("El DNI es obligatorio en esta versión.");
        return;
      }
      if (emailFinal && !validarEmail(emailFinal)) {
        setErrorEmail("Correo electrónico no válido");
        return;
      }
    }

    const payload = {
      nombre: nuevoNombre,
      apellidos: (nuevoApellidos || "").trim() || null,
      dni: dniLimpio || null,
      email: emailFinal,
      telefono: (nuevoTelefono || "").trim() || null,
    };

    try {
      // 1) Actualizar datos básicos
      const res = await fetch(`${Global.url}usuarios/editar/${usuarioSeleccionado.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        console.error("Error al editar usuario:", res.status);
        return;
      }

      // 2) Si hay un avatar nuevo seleccionado, subirlo
      if (avatarFileEdit) {
        try {
          const formData = new FormData();
          formData.append("avatar", avatarFileEdit);

          const avatarRes = await fetch(
            `${Global.url}usuarios/${usuarioSeleccionado.id}/avatar`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
              body: formData,
            }
          );

          const avatarData = await avatarRes.json().catch(() => ({}));
          if (!avatarRes.ok) {
            console.error("Error al subir avatar en edición:", avatarData);
          }
        } catch (err) {
          console.error("Error de red al subir avatar (edición):", err);
        }
      }

      // 3) Refrescar listado
      await fetchUsuarios();
    } catch (e) {
      console.error(e);
    } finally {
      setModalEditar(false);
      setAvatarFileEdit(null);
    }
  };

  const eliminarUsuario = async (id) => {
    if (!window.confirm("¿Eliminar este usuario?")) return;
    try {
      const res = await fetch(`${Global.url}usuarios/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (res.ok) await fetchUsuarios();
    } catch (e) {
      console.error(e);
    }
  };

  const reactivarUsuario = async (id) => {
    if (!window.confirm("¿Reactivar este usuario?")) return;
    try {
      const res = await fetch(`${Global.url}usuarios/reactivar/${id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (res.ok) await fetchUsuarios();
    } catch (e) {
      console.error(e);
    }
  };

  // ---------- Render ----------
  return (
    <div className="gestion-usuarios">
      <h2 className="centrar-titulo">Gestión de Usuarios</h2>

      <div className="filtros">
        <div className="filtro">
          <label>Usuario:</label>
          <Select
            options={usuarioOptions}
            value={valueFrom(usuarioOptions, filtroUsuario)}
            onChange={(opt) => setFiltroUsuario(opt?.value || "")}
            isClearable
            isSearchable
            classNamePrefix="select"
            placeholder="Todos"
          />
        </div>

        <div className="filtro">
          <label>Rol:</label>
          <Select
            options={rolOptions}
            value={valueFrom(rolOptions, filtroRol)}
            onChange={(opt) => setFiltroRol(opt?.value || "")}
            isClearable
            isSearchable
            classNamePrefix="select"
            placeholder="Todos"
          />
        </div>

        <div className="filtro">
          <label>Manager:</label>
          <Select
            options={managerOptions}
            value={valueFrom(managerOptions, filtroManager)}
            onChange={(opt) => setFiltroManager(opt?.value || "")}
            isClearable
            isSearchable
            classNamePrefix="select"
            placeholder="Todos"
          />
        </div>

        <div className="filtro filtro-checkbox-inline">
          <label htmlFor="chk-desact">Mostrar desactivados</label>
          <input
            id="chk-desact"
            type="checkbox"
            checked={mostrarDesactivados}
            onChange={(e) => setMostrarDesactivados(e.target.checked)}
          />
        </div>
      </div>

      <div className="table-listado-padre">
        <table className="table-listado table-listado--wide">
          <thead>
            <tr>
              {/* Columna foto sin texto */}
              <th className="th-avatar"></th>

              {/* Nombre va aquí */}
              <th className="ordenable" onClick={() => manejarOrden("nombre")}>Nombre</th>
              <th className="ordenable" onClick={() => manejarOrden("apellidos")}>Apellidos</th>
              <th className="ordenable" onClick={() => manejarOrden("email")}>Email</th>

              {/* DNI movido aquí */}
              <th className="ordenable" onClick={() => manejarOrden("dni")}>DNI</th>

              <th className="ordenable" onClick={() => manejarOrden("telefono")}>Teléfono</th>
              <th className="ordenable" onClick={() => manejarOrden("rol")}>Rol</th>
              <th className="ordenable" onClick={() => manejarOrden("manager")}>Manager</th>

              <th>Acciones</th>
              {mostrarDesactivados && <th>Desactivado</th>}

              <th className="td-invisible"></th>
            </tr>
          </thead>

          <tbody>
            {usuariosOrdenados.map((usuario) =>
              (usuario.activo || mostrarDesactivados) ? (
                <tr key={usuario.id} style={{ opacity: usuario.activo ? 1 : 0.6 }}>
                  {/* FOTO */}
                  <td>
                    <div className="avatar-lista">
                      {getAvatarUrl(usuario) ? (
                        <img
                          src={getAvatarUrl(usuario)}
                          alt={`${usuario.nombre} ${usuario.apellidos || ""}`}
                        />
                      ) : (
                        <div className="avatar-fallback">
                          {getIniciales(usuario)}
                        </div>
                      )}
                    </div>
                  </td>

                  {/* NOMBRE */}
                  <td>{usuario.nombre}</td>

                  {/* APELLIDOS */}
                  <td>{usuario.apellidos}</td>

                  {/* EMAIL */}
                  <td>{usuario.email}</td>

                  {/* DNI */}
                  <td>{usuario.dni}</td>

                  {/* RESTO */}
                  <td>{usuario.telefono}</td>
                  <td>{usuario.rol}</td>
                  <td>{usuario.manager || "Sin manager"}</td>

                  <td className="botones-usuario">
                    <button className="btn-mapa" onClick={() => abrirModalRol(usuario)}>Editar Rol</button>
                    <button className="btn-mapa" onClick={() => abrirModalManager(usuario)}>Asignar Manager</button>
                    <button className="btn-mapa" onClick={() => abrirModalEditar(usuario)}>Modificar</button>
                    {usuario.activo ? (
                      <button className="btn-mapa" onClick={() => eliminarUsuario(usuario.id)}>Eliminar</button>
                    ) : (
                      <button className="btn-mapa" onClick={() => reactivarUsuario(usuario.id)}>Reactivar</button>
                    )}
                  </td>

                  {mostrarDesactivados ? (
                    <td>{usuario.activo ? "" : "Desactivado"}</td>
                  ) : null}

                  <td className="td-invisible"></td>
                </tr>
              ) : null
            )}
          </tbody>
        </table>
      </div>

      {/* MODAL: Editar Usuario */}
      {modalEditar && usuarioSeleccionado && (
        <div className="modal-overlay">
          <div className="modal-content formulario-usuario-editar">
            <button className="modal-close" onClick={() => setModalEditar(false)}>&times;</button>
            <h2 className="centrar-titulo">Editar Usuario</h2>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                editarUsuario();
              }}
            >
              <div className="form-group">
                <label>
                  DNI{REQUIRE_DNI ? " (obligatorio)" : ""}:
                </label>
                <input
                  type="text"
                  value={nuevoDni}
                  onChange={(e) => setNuevoDni(e.target.value)}
                  required={REQUIRE_DNI}
                  placeholder="12345678Z"
                />
              </div>

              <div className="form-group">
                <label>Nombre:</label>
                <input
                  type="text"
                  value={nuevoNombre}
                  onChange={(e) => setNuevoNombre(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Apellidos:</label>
                <input
                  type="text"
                  value={nuevoApellidos}
                  onChange={(e) => setNuevoApellidos(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>
                  Email{REQUIRE_MAIL ? " (obligatorio)" : ""}:
                </label>
                <input
                  type="email"
                  value={nuevoEmail}
                  onChange={(e) => setNuevoEmail(e.target.value)}
                  required={REQUIRE_MAIL}
                  placeholder="usuario@dominio.com"
                />
              </div>

              <div className="form-group">
                <label>Teléfono:</label>
                <input
                  type="text"
                  value={nuevoTelefono}
                  onChange={(e) => setNuevoTelefono(e.target.value)}
                  placeholder="Ej: 600 123 456"
                />
              </div>

              {/* 🆕 Avatar en edición */}
              <div className="form-group">
                <label>Foto / Avatar:</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={onAvatarEditChange}
                />
                {avatarFileEdit && (
                  <small className="muted">
                    Seleccionado: {avatarFileEdit.name} ({Math.round(avatarFileEdit.size / 1024)} KB)
                  </small>
                )}
              </div>

              <div className="form-group full-width">
                {errorMensaje && <p className="rojo">{errorMensaje}</p>}
                {errorEmail && <p className="rojo">{errorEmail}</p>}
                <button className="btn-login" type="submit">Guardar Cambios</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: Editar Rol */}
      {modalRol && usuarioSeleccionado && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="modal-close" onClick={() => setModalRol(false)}>&times;</button>
            <h3>Cambiar Rol de {usuarioSeleccionado.nombre}</h3>
            <div className="modal-options">
              <select value={nuevoRol} onChange={(e) => setNuevoRol(e.target.value)}>
                <option value="usuario">Usuario</option>
                <option value="gerente">Gerente</option>
                <option value="admin">Administrador</option>
              </select>
              <button onClick={actualizarRol}>Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Asignar Manager */}
      {modalManager && usuarioSeleccionado && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="modal-close" onClick={() => setModalManager(false)}>&times;</button>
            <h3>Asignar Manager a {usuarioSeleccionado.nombre}</h3>
            <div className="modal-options">
              <select
                value={nuevoManager}
                onChange={(e) => setNuevoManager(e.target.value)}
              >
                <option value="">Sin manager</option>
                {usuariosLocal
                  .filter((u) => u.rol === "gerente")
                  .map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.nombre} {g.apellidos || ""}
                    </option>
                  ))}
              </select>
              <button onClick={asignarManager}>Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
